/**
 * sync.ts
 * Handles syncing edited SOG files back to R2 storage with cache invalidation
 */

import { Events } from './events';
import { BufferWriter } from './serialize/writer';
import { serializeSog, SogSettings } from './splat-serialize';
import { Splat } from './splat';

const DB_WORKER_URL = 'https://d1-database-worker.xor3dai.workers.dev';

interface SyncSession {
    token: string | null;
    assetId: string;
    userId: string;
    email: string;
}

interface UploadUrlResponse {
    uploadUrl: string;
    key: string;
    assetId: string;
    expiresIn: number;
}

interface SyncResult {
    success: boolean;
    message: string;
    cachePurged?: boolean;
}

/**
 * Get the current editor session from window and URL
 */
function getSession(): SyncSession | null {
    // Get asset ID from URL
    const params = new URLSearchParams(window.location.search);
    const assetId = params.get('id');
    const token = params.get('token');
    
    if (!assetId) {
        console.error('Sync: No asset ID in URL');
        return null;
    }
    
    // Get session from window (set in main.ts)
    const session = (window as any).editorSession;
    
    return {
        token: token,
        assetId: assetId,
        userId: session?.user_id || 'test-user',
        email: session?.email || 'test@test.com'
    };
}

/**
 * Get presigned upload URL from the worker
 * For testing: if no token, use a direct endpoint
 */
async function getUploadUrl(session: SyncSession): Promise<UploadUrlResponse> {
    console.log('Sync: Getting upload URL for asset:', session.assetId);
    
    const response = await fetch(`${DB_WORKER_URL}/get-upload-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            token: session.token || '',
            assetId: session.assetId
        })
    });

    const data = await response.json();
    console.log('Sync: Upload URL response:', data);

    if (!response.ok) {
        throw new Error(data.error || `HTTP ${response.status}`);
    }

    return data;
}

/**
 * Export the scene as SOG format and return as Blob
 */
async function exportSceneAsSog(events: Events): Promise<Blob> {
    console.log('Sync: Checking if scene is empty...');
    
    // Check if scene has splats
    const isEmpty = await events.invoke('scene.empty');
    console.log('Sync: Scene empty?', isEmpty);
    
    if (isEmpty) {
        throw new Error('Scene is empty - nothing to sync');
    }

    // Get the splats from the scene
    console.log('Sync: Getting splats from scene...');
    const splats: Splat[] = await events.invoke('scene.splats');
    console.log('Sync: Got splats:', splats?.length);

    if (!splats || splats.length === 0) {
        throw new Error('No visible splats in scene');
    }

    console.log('Sync: Creating BufferWriter...');
    
    // Create a BufferWriter to capture the SOG data
    const writer = new BufferWriter();
    
    console.log('Sync: Calling serializeSog directly...');
    
    // SOG serialization settings
    // Note: Don't pass 'events' to avoid showing the PlayCanvas progress dialog
    const sogSettings: SogSettings = {
        minOpacity: 1 / 255,
        removeInvalid: true,
        iterations: 10
        // events: events  <-- omitted to hide progress dialog
    };
    
    // Call serializeSog directly (same as file-handler.ts does)
    await serializeSog(splats, sogSettings, writer);
    
    console.log('Sync: Closing writer...');
    
    // close() returns Uint8Array[] - array of buffers
    const buffers = writer.close();
    
    console.log('Sync: Got buffers:', buffers?.length, 'chunks');
    
    if (!buffers || buffers.length === 0) {
        throw new Error('Failed to export scene - empty data');
    }
    
    // Create a Blob from the buffers (same approach as DownloadWriter)
    // Cast to BlobPart[] to satisfy TypeScript
    const blob = new Blob(buffers as BlobPart[], { type: 'application/octet-stream' });
    
    console.log('Sync: Created blob, size:', blob.size);
    
    if (blob.size === 0) {
        throw new Error('Failed to export scene - empty blob');
    }
    
    return blob;
}

/**
 * Upload the SOG file to R2 using presigned URL
 */
async function uploadToR2(uploadUrl: string, data: Blob): Promise<void> {
    console.log('Sync: Uploading to R2, size:', data.size);
    
    const response = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/octet-stream'
        },
        body: data
    });

    console.log('Sync: Upload response status:', response.status);

    if (!response.ok) {
        throw new Error(`Upload failed: HTTP ${response.status}`);
    }
}

/**
 * Confirm sync and trigger cache purge
 */
async function confirmSync(session: SyncSession): Promise<SyncResult> {
    console.log('Sync: Confirming sync and purging cache...');
    
    const response = await fetch(`${DB_WORKER_URL}/confirm-sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            token: session.token || '',
            assetId: session.assetId
        })
    });

    const data = await response.json();
    console.log('Sync: Confirm sync response:', data);

    if (!response.ok) {
        throw new Error(data.error || `HTTP ${response.status}`);
    }

    return data;
}

/**
 * Main sync function - orchestrates the entire sync process
 */
export async function syncScene(events: Events, onProgress?: (message: string) => void): Promise<SyncResult> {
    const progress = (msg: string) => {
        console.log('Sync progress:', msg);
        onProgress?.(msg);
    };
    
    console.log('========== SYNC STARTED ==========');
    
    try {
        // Step 1: Get session
        progress('Validating session...');
        const session = getSession();
        console.log('Sync: Session:', session);
        
        if (!session) {
            throw new Error('No asset ID found in URL. Please open the editor with ?id=assetId');
        }

        // Step 2: Get upload URL
        progress('Preparing upload...');
        const { uploadUrl } = await getUploadUrl(session);

        // Step 3: Export scene as SOG
        progress('Exporting scene...');
        const sogBlob = await exportSceneAsSog(events);
        progress(`Scene exported (${(sogBlob.size / 1024 / 1024).toFixed(2)} MB)`);

        // Step 4: Upload to R2
        progress('Uploading to cloud...');
        await uploadToR2(uploadUrl, sogBlob);

        // Step 5: Confirm sync and purge cache
        progress('Finalizing sync...');
        const result = await confirmSync(session);

        progress('Sync complete!');
        console.log('========== SYNC COMPLETED ==========');
        
        return {
            success: true,
            message: 'Scene synced successfully! Viewers will see the update.',
            cachePurged: result.cachePurged
        };

    } catch (err: any) {
        console.error('========== SYNC FAILED ==========');
        console.error('Sync error:', err);
        return {
            success: false,
            message: err.message || 'Sync failed'
        };
    }
}

/**
 * Register sync events with the event system
 */
export function registerSyncEvents(events: Events): void {
    // Register the sync function
    events.function('scene.sync', async () => {
        return syncScene(events);
    });

    // Register sync with progress callback
    events.function('scene.syncWithProgress', async (onProgress: (message: string) => void) => {
        return syncScene(events, onProgress);
    });
}